/**
 * Lazy import wrapper with retry logic for chunk loading failures
 *
 * When a deployment happens, chunk hashes change. If a user has the old HTML
 * cached, their browser will try to load old chunks that no longer exist.
 * This wrapper automatically retries with a hard reload when that happens.
 */

import React from 'react';

interface RetryOptions {
  maxRetries?: number;
  delay?: number;
}

export function lazyRetry<T extends React.ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  options: RetryOptions = {}
): React.LazyExoticComponent<T> {
  const { maxRetries = 3, delay = 1000 } = options;

  return React.lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    );

    try {
      const component = await componentImport();

      // Reset the flag on successful load
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');

      return component;
    } catch (error) {
      // Check if this is a chunk loading error
      const isChunkLoadError =
        error instanceof Error &&
        (error.message.includes('Failed to fetch dynamically imported module') ||
         error.message.includes('error loading dynamically imported module') ||
         error.message.includes('Importing a module script failed'));

      if (isChunkLoadError) {
        console.warn('Chunk load error detected:', error);

        // If we haven't already force refreshed, do it now
        if (!pageHasAlreadyBeenForceRefreshed) {
          window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');

          // Force reload the page to get the new chunks
          window.location.reload();

          // Return a dummy component while reloading
          return {
            default: (() => null) as T,
          };
        }

        // If we already tried refreshing and it still failed, retry the import

        let lastError = error;
        for (let i = 0; i < maxRetries; i++) {
          try {
            await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
            return await componentImport();
          } catch (retryError) {
            console.warn(`Retry ${i + 1}/${maxRetries} failed:`, retryError);
            lastError = retryError as Error;
          }
        }

        // All retries failed, throw the last error
        throw lastError;
      }

      // Not a chunk loading error, throw immediately
      throw error;
    }
  });
}

// Re-export React.lazy for consistency
export { lazy } from 'react';
